/**
 * Cube dialer environment helpers: URLs, connector variant, UI status labels,
 * and reading auto-transfer profile from localStorage remarks.cubeDetails.
 */
import { environment } from 'src/environments/environment';
import { shouldUseCubeSdk, getCubeBridgeIframeId } from '../cube-sdk/cube-sdk-mode';
import {
  CubeAutoTransferProfile,
  isCubeAutoTransferConfigReady,
  isCubeAutoTransferSourceCampaign as isCubeJsonSourceCampaign,
  isCubeAutoTransferDummyEligible,
  resolveCubeAutoTransferProfile,
} from './cube-auto-transfer-config';

export type { CubeAutoTransferProfile } from './cube-auto-transfer-config';

export type CubeDialerMode = 'api' | 'connector';
export type CubeConnectorVariant = 'popup' | 'webphone';

export type CubeConnectorUiStatus =
  | 'initializing'
  | 'loading_webphone'
  | 'logging_in'
  | 'ready'
  | 'failed'
  | 'logged_out'
  | 'on_call'
  | 'on_break';

export function cubeConnectorStatusLabel(status: CubeConnectorUiStatus): string {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'loading_webphone':
      return 'Loading webphone...';
    case 'logging_in':
      return 'Logging in...';
    case 'failed':
      return 'Login failed — tap Retry';
    case 'logged_out':
      return 'Logged out';
    case 'on_call':
      return 'On call';
    case 'on_break':
      return 'On break';
    default:
      return 'Connecting...';
  }
}

export function cubeConnectorStatusIsLoggedIn(status: CubeConnectorUiStatus): boolean {
  return status === 'ready' || status === 'on_call' || status === 'on_break';
}

export function cubeConnectorStatusIsReady(status: CubeConnectorUiStatus): boolean {
  return status === 'ready' || status === 'on_call' || status === 'on_break';
}

export const CUBE_DEFAULT_DISPOSITION = 'No Requirment';

export const CUBE_WEBPHONE_DEFAULT_URL = 'https://quickdialer.in/cube175Webphone';
export const CUBE_WEBPHONE_PATH = 'cube175Webphone';
export const CUBE_WEBPHONE_URL = CUBE_WEBPHONE_DEFAULT_URL;
export const CUBE_CONNECTOR_SCRIPT = 'https://cubeivr.in/quickcall-connector/cube-connector-v2.js';
export const CUBE_CONNECTOR_CHILD_ORIGIN = 'https://cubeivr.in';
export const CUBE_POPUP_DEFAULT_URL = 'https://cubehosted.net/cube/';

export function isCubeDialerAllowed(): boolean {
  return !!(environment as { isCubeDialerAllowed?: boolean }).isCubeDialerAllowed;
}

export function getCubeDialerMode(): CubeDialerMode {
  return isCubeDialerAllowed() ? 'connector' : 'api';
}

export function isCubeConnectorDialer(): boolean {
  return isCubeDialerAllowed();
}

export function isCubeApiDialer(): boolean {
  return false;
}

export function getCubeConnectorVariant(): CubeConnectorVariant {
  return 'webphone';
}

export function isCubePopupConnector(): boolean {
  return false;
}

export function isCubeWebphoneConnector(): boolean {
  return isCubeDialerAllowed();
}

export function getCubeAutoAnswerEnabled(): boolean {
  return !!(environment as { cube_auto_answer?: boolean }).cube_auto_answer;
}

/** Reads remarks.cubeDetails; target agent resolved when online list is passed in. */
export function getCubeAutoTransferProfile(): CubeAutoTransferProfile {
  const empty: CubeAutoTransferProfile = {
    enabled: false,
    targetAgentId: '',
    targetCampaign: '',
    loginCampaign: '',
  };
  try {
    const raw = localStorage.getItem('remarks');
    if (!raw) {
      return empty;
    }
    const details = JSON.parse(raw)?.cubeDetails || {};
    const loginCampaign = String(
      details.CampaignId ||
        details.campaignId ||
        details.CampaignID ||
        details.campaignID ||
        details.Campaign ||
        details.campaign ||
        ''
    ).trim();
    const agentId = String(details.AgentId || details.agentId || details.agentID || '').trim();
    if (!loginCampaign || !agentId) {
      return empty;
    }
    return resolveCubeAutoTransferProfile(agentId, loginCampaign);
  } catch {
    return empty;
  }
}

/** True when logged-in Cube agent is a configured dummy user (auto-transfer session). */
export function isCubeAutoTransferDummySession(): boolean {
  try {
    const raw = localStorage.getItem('remarks');
    if (!raw) {
      return false;
    }
    const details = JSON.parse(raw)?.cubeDetails || {};
    const loginCampaign = String(
      details.CampaignId ||
        details.campaignId ||
        details.CampaignID ||
        details.campaignID ||
        details.Campaign ||
        details.campaign ||
        ''
    ).trim();
    const agentId = String(details.AgentId || details.agentId || details.agentID || '').trim();
    return isCubeAutoTransferDummyEligible(agentId, loginCampaign);
  } catch {
    return false;
  }
}

export function isCubeAutoTransferSourceCampaign(campaignName: string): boolean {
  return isCubeJsonSourceCampaign(campaignName);
}

export function getCubePopupBaseUrl(): string {
  return CUBE_POPUP_DEFAULT_URL;
}

export function isCubeWebphoneProxyEnabled(): boolean {
  const env = environment as { cube_webphone_use_proxy?: boolean };
  return env.cube_webphone_use_proxy !== false;
}

export function getCubeWebphoneUrl(): string {
  const env = environment as {
    cube_webphone_use_proxy?: boolean;
    cube_webphone_url?: string;
    api_url?: string;
  };
  const useProxy = isCubeWebphoneProxyEnabled() && !!env.api_url;
  if (useProxy) {
    return `${env.api_url.replace(/\/$/, '')}/api/cube-webphone/${CUBE_WEBPHONE_PATH}/`;
  }
  return (env.cube_webphone_url || CUBE_WEBPHONE_DEFAULT_URL).replace(/\/$/, '');
}

export function getCubeConnectorScriptUrl(): string {
  return (environment as { cube_connector_script?: string }).cube_connector_script || CUBE_CONNECTOR_SCRIPT;
}

export function getCubeConnectorChildOrigin(): string {
  const env = environment as {
    cube_webphone_use_proxy?: boolean;
    api_url?: string;
    cube_connector_child_origin?: string;
  };
  if (isCubeWebphoneProxyEnabled() && env.api_url) {
    return env.api_url.replace(/\/$/, '');
  }
  return env.cube_connector_child_origin || CUBE_CONNECTOR_CHILD_ORIGIN;
}

export function getCubeIframePostMessageTarget(iframe?: HTMLIFrameElement | null): string {
  try {
    const src = iframe?.src || (document.getElementById(getCubeBridgeIframeId()) as HTMLIFrameElement | null)?.src;
    if (src) {
      return new URL(src, window.location.href).origin;
    }
  } catch (_e) {}
  return getCubeConnectorChildOrigin() || '*';
}

export function isCubeIframeVisible(): boolean {
  if (!isCubeDialerAllowed()) {
    return false;
  }
  if (shouldUseCubeSdk()) {
    return !!(environment as { isCubeIframeVisible?: boolean }).isCubeIframeVisible;
  }
  try {
    if (localStorage.getItem('moneytor_cube_iframe_visible') === 'true') {
      return true;
    }
    if (localStorage.getItem('moneytor_cube_iframe_debug') === 'true') {
      return true;
    }
  } catch (_e) {}
  return !!(environment as { isCubeIframeVisible?: boolean }).isCubeIframeVisible;
}

export function isCubeIframeDebugEnabled(): boolean {
  try {
    return localStorage.getItem('moneytor_cube_iframe_debug') === 'true';
  } catch (_e) {
    return false;
  }
}

export function setCubeIframeVisible(visible: boolean | null): void {
  try {
    if (visible === null) {
      localStorage.removeItem('moneytor_cube_iframe_visible');
      return;
    }
    localStorage.setItem('moneytor_cube_iframe_visible', visible ? 'true' : 'false');
  } catch (_e) {}
}

export function shouldMountCubeConnectorBridge(): boolean {
  return isCubeDialerAllowed();
}

export interface CubeAgentConfig {
  agentId: string;
  extension: string;
  password: string;
  campaignId: string;
}

export function normalizeCubeDetails(raw: Record<string, unknown> | null | undefined): CubeAgentConfig | null {
  if (!raw) {
    return null;
  }
  const agentId = raw['AgentId'] || raw['agentId'] || raw['agentID'];
  const extension = raw['Extension'] || raw['extension'] || raw['agentExtn'];
  const password = raw['password'] || raw['Password'];
  const campaignId =
    raw['CampaignId'] ||
    raw['CampaignID'] ||
    raw['campaignId'] ||
    raw['campaignID'] ||
    raw['Campaign'] ||
    raw['campaign'];
  if (!agentId || !extension || !password || !campaignId) {
    return null;
  }
  return {
    agentId: String(agentId),
    extension: String(extension),
    password: String(password),
    campaignId: String(campaignId),
  };
}

export function describeMissingCubeCredentials(): string[] {
  const raw = localStorage.getItem('remarks');
  if (!raw) {
    return ['remarks missing in localStorage — log out and log in again'];
  }
  try {
    const remarks = JSON.parse(raw);
    const d = remarks?.cubeDetails;
    if (!d) {
      return ['remarks.cubeDetails missing — set Users.fieldJSON1.cubeDetails in DB and re-login'];
    }
    const missing: string[] = [];
    if (!(d.AgentId || d.agentId || d.agentID)) missing.push('AgentId');
    if (!(d.Extension || d.extension || d.agentExtn)) missing.push('Extension');
    if (!(d.password || d.Password)) missing.push('password');
    if (!(d.CampaignId || d.CampaignID || d.campaignId || d.campaignID || d.Campaign || d.campaign)) {
      missing.push('CampaignId');
    }
    return missing.length ? missing.map((k) => `cubeDetails.${k} missing`) : [];
  } catch (_e) {
    return ['remarks is not valid JSON'];
  }
}

export function persistCubeDetailsToRemarks(
  cubeDetails: Record<string, unknown>,
  agentIdOverride?: string
): CubeAgentConfig | null {
  const normalized = normalizeCubeDetails(cubeDetails);
  if (!normalized) {
    return null;
  }
  const raw = localStorage.getItem('remarks');
  let remarks: Record<string, unknown> = {};
  if (raw) {
    try {
      remarks = JSON.parse(raw);
    } catch (_e) {
      remarks = {};
    }
  }
  const prev =
    remarks['cubeDetails'] && typeof remarks['cubeDetails'] === 'object'
      ? (remarks['cubeDetails'] as Record<string, unknown>)
      : {};
  const transferTo = String(
    cubeDetails['toTranfer'] ||
      cubeDetails['toTransfer'] ||
      prev['toTranfer'] ||
      prev['toTransfer'] ||
      ''
  ).trim();
  const transferCampaign = String(
    cubeDetails['toTransferCampaign'] ||
      prev['toTransferCampaign'] ||
      ''
  ).trim();
  remarks['cubeDetails'] = {
    ...prev,
    AgentId: normalized.agentId,
    Extension: normalized.extension,
    password: normalized.password,
    CampaignId: normalized.campaignId,
    CampaignID: normalized.campaignId,
    campaignId: normalized.campaignId,
    campaignID: normalized.campaignId,
    isCallTransfer:
      cubeDetails['isCallTransfer'] !== undefined && cubeDetails['isCallTransfer'] !== null
        ? cubeDetails['isCallTransfer']
        : prev['isCallTransfer'],
    toTransferCampaign: transferCampaign,
    toTranfer: transferTo,
    toTransfer: transferTo,
  };
  remarks['agentID'] = (agentIdOverride || normalized.agentId).toLowerCase();
  localStorage.setItem('remarks', JSON.stringify(remarks));
  return normalized;
}

export function getCubeAgentConfigFromRemarks(): CubeAgentConfig | null {
  const raw = localStorage.getItem('remarks');
  if (!raw) return null;
  try {
    const remarks = JSON.parse(raw);
    return normalizeCubeDetails(remarks?.cubeDetails);
  } catch (_e) {
    return null;
  }
}

export function isCubeAgentConfigComplete(agent: CubeAgentConfig | null | undefined): agent is CubeAgentConfig {
  return !!(
    agent?.agentId?.trim() &&
    agent?.extension?.trim() &&
    agent?.password?.trim() &&
    agent?.campaignId?.trim()
  );
}

export function buildCubeSsoQueryParams(agent: CubeAgentConfig): URLSearchParams {
  return new URLSearchParams({
    agentID: agent.agentId,
    campaignID: agent.campaignId,
    agentExtn: agent.extension,
    password: agent.password,
    autoSSOlogin: 'true',
  });
}

export function buildCubePopupLoginUrl(agent: CubeAgentConfig): string {
  const base = getCubePopupBaseUrl().replace(/\/$/, '');
  return `${base}/?${buildCubeSsoQueryParams(agent).toString()}`;
}

export function buildCubeWebphoneLoginUrl(agent: CubeAgentConfig): string {
  const base = getCubeWebphoneUrl().replace(/\/$/, '');
  return `${base}?${buildCubeSsoQueryParams(agent).toString()}`;
}
